import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'

// Escáner de código de barras con la cámara del dispositivo.
// - Usa la cámara trasera (facingMode: environment)
// - Permite escanear varios códigos seguidos sin cerrar
// - Ignora lecturas repetidas del mismo código dentro de 2,5 s
// - Muestra un destello verde (encontrado) o rojo (no registrado) con mensaje.
//   El componente padre informa el resultado vía la prop `resultado`
//   (objeto { tipo: 'ok' | 'error', texto } que cambia en cada lectura).
export default function EscanerCodigo({ onDetectado, onCerrar, resultado }) {
  const [error, setError] = useState(null)
  const [flash, setFlash] = useState(null) // { tipo, texto }
  const ultimoRef = useRef({ codigo: null, t: 0 })

  useEffect(() => {
    const scanner = new Html5Qrcode('escaner-codigo-video')
    let montado = true

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 260, height: 160 } },
      texto => {
        const ahora = Date.now()
        const u = ultimoRef.current
        if (texto === u.codigo && ahora - u.t < 2500) return
        ultimoRef.current = { codigo: texto, t: ahora }
        onDetectado(texto)
      },
      () => {} // errores de frame individuales: ignorar (es normal mientras enfoca)
    ).catch(() => {
      if (montado) setError('No se pudo acceder a la cámara. Acepta el permiso de cámara cuando el navegador lo pida, o revisa Ajustes → Safari/Chrome → Cámara.')
    })

    return () => {
      montado = false
      scanner.stop().catch(() => {}).finally(() => {
        try { scanner.clear() } catch {}
      })
    }
  }, []) // se monta una sola vez: el escáner vive mientras el modal está abierto

  // Cuando el padre reporta un resultado, mostrar destello + vibración
  useEffect(() => {
    if (!resultado) return
    setFlash(resultado)
    if (navigator.vibrate) {
      navigator.vibrate(resultado.tipo === 'ok' ? 80 : [70, 60, 70]) // error = doble vibración
    }
    const t = setTimeout(() => setFlash(null), resultado.tipo === 'ok' ? 550 : 1600)
    return () => clearTimeout(t)
  }, [resultado])

  const flashColor = flash
    ? (flash.tipo === 'ok' ? 'rgba(26,122,74,.55)' : 'rgba(185,32,32,.6)')
    : 'transparent'

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.75)',
      zIndex:2500, display:'flex', alignItems:'center', justifyContent:'center',
      padding:16
    }}>
      <div className="card" style={{width:'100%', maxWidth:440, padding:16}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
          <div style={{fontWeight:600, fontSize:14}}>📷 Escanear código de barras</div>
          <button onClick={onCerrar}
            style={{background:'none', border:'none', cursor:'pointer', fontSize:18, color:'var(--color-muted)'}}>
            ✕
          </button>
        </div>

        {error ? (
          <div style={{
            background:'var(--color-danger-bg)', color:'var(--color-danger)',
            padding:'12px 14px', borderRadius:'var(--radius-sm)', fontSize:13, marginBottom:12
          }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{position:'relative', marginBottom:10}}>
              <div id="escaner-codigo-video"/>
              {/* Destello de color sobre la cámara */}
              <div style={{
                position:'absolute', inset:0, borderRadius:'var(--radius-md)',
                background: flashColor,
                transition: flash ? 'background .05s' : 'background .4s',
                pointerEvents:'none'
              }}/>
            </div>

            {/* Mensaje del resultado */}
            {flash ? (
              <div style={{
                textAlign:'center', marginBottom:12, padding:'10px 12px',
                borderRadius:'var(--radius-sm)', fontSize:13, fontWeight:600,
                background: flash.tipo === 'ok' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                color: flash.tipo === 'ok' ? 'var(--color-success)' : 'var(--color-danger)'
              }}>
                {flash.tipo === 'ok' ? '\u2713 ' : '\u26a0 '}{flash.texto}
              </div>
            ) : (
              <p style={{fontSize:12, color:'var(--color-muted)', textAlign:'center', marginBottom:12}}>
                Apunta al código de barras. Puedes escanear varios productos seguidos.
              </p>
            )}
          </>
        )}

        <button className="btn btn-primary btn-block" onClick={onCerrar}>
          Listo
        </button>
      </div>
    </div>
  )
}
